import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Eye, EyeOff, Save, X } from 'lucide-react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { useToast } from '../../hooks/use-toast';
import AdminLayout from './AdminLayout';

const AdminGallery = () => {
  const { adminToken } = useAdminAuth();
  const { toast } = useToast();
  const [liquors, setLiquors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingLiquor, setEditingLiquor] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'Gin',
    image: '',
    description: '',
    botanicals: [],
    client: '',
    bottles: '',
    active: true
  });
  const [botanicalInput, setBotanicalInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    loadGallery();
  }, []);

  const loadGallery = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/gallery/admin/all`,
        {
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setLiquors(data);
      }
    } catch (error) {
      console.error('Error loading gallery:', error);
      toast({
        title: "Errore",
        description: "Impossibile caricare la gallery",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const url = editingLiquor
        ? `${process.env.REACT_APP_BACKEND_URL}/api/gallery/admin/${editingLiquor.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/gallery/admin/create`;

      const response = await fetch(url, {
        method: editingLiquor ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify({
          ...formData,
          bottles: formData.bottles ? parseInt(formData.bottles) : null
        })
      });

      if (response.ok) {
        toast({
          title: "Successo",
          description: editingLiquor ? "Liquore aggiornato" : "Liquore creato"
        });
        setShowModal(false);
        resetForm();
        loadGallery();
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      toast({
        title: "Errore",
        description: "Impossibile salvare il liquore",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo liquore?')) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/gallery/admin/${id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );

      if (response.ok) {
        toast({
          title: "Successo",
          description: "Liquore eliminato"
        });
        loadGallery();
      }
    } catch (error) {
      toast({
        title: "Errore",
        description: "Impossibile eliminare il liquore",
        variant: "destructive"
      });
    }
  };

  const handleToggleActive = async (id) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/gallery/admin/${id}/toggle-active`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );

      if (response.ok) {
        toast({
          title: "Successo",
          description: "Stato aggiornato"
        });
        loadGallery();
      }
    } catch (error) {
      toast({
        title: "Errore",
        description: "Impossibile aggiornare lo stato",
        variant: "destructive"
      });
    }
  };

  const handleEdit = (liquor) => {
    setEditingLiquor(liquor);
    setFormData({
      name: liquor.name,
      type: liquor.type,
      image: liquor.image,
      description: liquor.description,
      botanicals: liquor.botanicals || [],
      client: liquor.client || '',
      bottles: liquor.bottles?.toString() || '',
      active: liquor.active
    });
    setImagePreview(liquor.image);
    setShowModal(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type (only images)
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Errore",
        description: "Seleziona un file immagine valido",
        variant: "destructive"
      });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "Errore",
        description: "L'immagine deve essere inferiore a 5MB",
        variant: "destructive"
      });
      return;
    }

    setUploading(true);

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const uniqueId = `gallery_${Date.now()}`;
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/upload/admin/gallery/${uniqueId}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${adminToken}`
          },
          body: formDataUpload
        }
      );

      if (response.ok) {
        const data = await response.json();
        const s3Key = data.filePath || data.url || data.path;
        
        // Construct the serving URL for S3 files
        const imageUrl = `${process.env.REACT_APP_BACKEND_URL}/api/upload/serve/${s3Key}`;
        
        setFormData({
          ...formData,
          image: imageUrl
        });
        
        // Set preview with serving URL
        setImagePreview(imageUrl);
        
        toast({
          title: "Successo",
          description: "Immagine caricata su S3"
        });
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      toast({
        title: "Errore",
        description: "Errore nel caricamento dell'immagine",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setEditingLiquor(null);
    setFormData({
      name: '',
      type: 'Gin',
      image: '',
      description: '',
      botanicals: [],
      client: '',
      bottles: '',
      active: true
    });
    setBotanicalInput('');
    setImagePreview(null);
  };

  const addBotanical = () => {
    if (botanicalInput.trim()) {
      setFormData({
        ...formData,
        botanicals: [...formData.botanicals, botanicalInput.trim()]
      });
      setBotanicalInput('');
    }
  };

  const removeBotanical = (index) => {
    setFormData({
      ...formData,
      botanicals: formData.botanicals.filter((_, i) => i !== index)
    });
  };

  // Helper function to get the correct image URL
  const getImageUrl = (imagePathOrUrl) => {
    if (!imagePathOrUrl) return '';
    
    // If it's already a full URL, return as is
    if (imagePathOrUrl.startsWith('http://') || imagePathOrUrl.startsWith('https://')) {
      return imagePathOrUrl;
    }
    
    // If it starts with /api/upload/serve/, it's already a serving URL
    if (imagePathOrUrl.startsWith('/api/upload/serve/')) {
      return `${process.env.REACT_APP_BACKEND_URL}${imagePathOrUrl}`;
    }
    
    // Otherwise, assume it's an S3 key and construct the serving URL
    return `${process.env.REACT_APP_BACKEND_URL}/api/upload/serve/${imagePathOrUrl}`;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-8">Caricamento...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestione Gallery</h1>
          <p className="text-gray-600">Gestisci i liquori della gallery pubblica</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="flex items-center bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuovo Liquore
        </button>
      </div>

      {/* Liquors Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {liquors.map((liquor) => (
          <div key={liquor.id} className="bg-white rounded-lg shadow-md overflow-hidden">
            {/* Image */}
            <div className="relative aspect-square">
              <img
                src={getImageUrl(liquor.image)}
                alt={liquor.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-2 right-2">
                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                  liquor.active ? 'bg-green-500 text-white' : 'bg-gray-500 text-white'
                }`}>
                  {liquor.active ? 'Attivo' : 'Inattivo'}
                </span>
              </div>
            </div>

            {/* Content */}
            <div className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-purple-600">{liquor.type}</span>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">{liquor.name}</h3>
              <p className="text-sm text-gray-600 mb-3 line-clamp-2">{liquor.description}</p>
              
              {liquor.client && (
                <p className="text-xs text-gray-500 mb-1">Cliente: {liquor.client}</p>
              )}
              {liquor.bottles && (
                <p className="text-xs text-gray-500 mb-3">{liquor.bottles} bottiglie</p>
              )}

              {/* Actions */}
              <div className="flex gap-2">
                <button
                  onClick={() => handleToggleActive(liquor.id)}
                  className="flex-1 flex items-center justify-center bg-gray-100 text-gray-700 px-3 py-2 rounded text-sm hover:bg-gray-200"
                >
                  {liquor.active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
                <button
                  onClick={() => handleEdit(liquor)}
                  className="flex-1 flex items-center justify-center bg-blue-100 text-blue-700 px-3 py-2 rounded text-sm hover:bg-blue-200"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(liquor.id)}
                  className="flex-1 flex items-center justify-center bg-red-100 text-red-700 px-3 py-2 rounded text-sm hover:bg-red-200"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold">
                  {editingLiquor ? 'Modifica Liquore' : 'Nuovo Liquore'}
                </h2>
                <button onClick={() => setShowModal(false)}>
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium mb-1">Nome *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2"
                    required
                  />
                </div>

                {/* Type */}
                <div>
                  <label className="block text-sm font-medium mb-1">Tipo *</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({...formData, type: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    <option>Gin</option>
                    <option>Amaro</option>
                    <option>Bitter</option>
                    <option>Limoncello</option>
                    <option>Liquore</option>
                  </select>
                </div>

                {/* Image Upload */}
                <div>
                  <label className="block text-sm font-medium mb-1">Immagine (quadrata) *</label>
                  
                  {/* Image Preview */}
                  {imagePreview && (
                    <div className="mb-3 relative">
                      <div className="aspect-square w-48 mx-auto rounded-lg overflow-hidden border-2 border-gray-200">
                        <img
                          src={getImageUrl(imagePreview)}
                          alt="Preview"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setImagePreview(null);
                          setFormData({...formData, image: ''});
                        }}
                        className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  
                  {/* Upload Button */}
                  <div className="space-y-2">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      id="image-upload"
                      disabled={uploading}
                    />
                    <label
                      htmlFor="image-upload"
                      className={`flex items-center justify-center border-2 border-dashed rounded-lg px-4 py-3 cursor-pointer transition-colors ${
                        uploading 
                          ? 'bg-gray-100 cursor-wait' 
                          : 'border-gray-300 hover:border-purple-500 hover:bg-purple-50'
                      }`}
                    >
                      {uploading ? (
                        <span className="text-gray-600">Caricamento...</span>
                      ) : (
                        <>
                          <Plus className="w-5 h-5 mr-2 text-gray-600" />
                          <span className="text-gray-600">
                            {imagePreview ? 'Cambia immagine' : 'Carica immagine'}
                          </span>
                        </>
                      )}
                    </label>
                    <p className="text-xs text-gray-500 text-center">
                      Formato quadrato consigliato • Max 5MB • JPG, PNG, WEBP
                    </p>
                  </div>

                  {/* Alternative: Manual URL Input */}
                  <div className="mt-3">
                    <label className="block text-xs text-gray-500 mb-1">Oppure inserisci URL manualmente:</label>
                    <input
                      type="url"
                      value={formData.image}
                      onChange={(e) => {
                        setFormData({...formData, image: e.target.value});
                        if (e.target.value) setImagePreview(e.target.value);
                      }}
                      className="w-full border rounded-lg px-3 py-2 text-sm"
                      placeholder="https://..."
                    />
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium mb-1">Descrizione *</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2"
                    rows="3"
                    required
                  />
                </div>

                {/* Botanicals */}
                <div>
                  <label className="block text-sm font-medium mb-1">Botaniche</label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={botanicalInput}
                      onChange={(e) => setBotanicalInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addBotanical())}
                      className="flex-1 border rounded-lg px-3 py-2"
                      placeholder="Aggiungi botanica..."
                    />
                    <button
                      type="button"
                      onClick={addBotanical}
                      className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData.botanicals.map((bot, index) => (
                      <span key={index} className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm flex items-center">
                        {bot}
                        <button
                          type="button"
                          onClick={() => removeBotanical(index)}
                          className="ml-2"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                {/* Client */}
                <div>
                  <label className="block text-sm font-medium mb-1">Cliente</label>
                  <input
                    type="text"
                    value={formData.client}
                    onChange={(e) => setFormData({...formData, client: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="Nome cliente..."
                  />
                </div>

                {/* Bottles */}
                <div>
                  <label className="block text-sm font-medium mb-1">Numero Bottiglie</label>
                  <input
                    type="number"
                    value={formData.bottles}
                    onChange={(e) => setFormData({...formData, bottles: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2"
                    min="0"
                  />
                </div>

                {/* Active */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="active"
                    checked={formData.active}
                    onChange={(e) => setFormData({...formData, active: e.target.checked})}
                    className="mr-2"
                  />
                  <label htmlFor="active" className="text-sm font-medium">Attivo (visibile nella gallery pubblica)</label>
                </div>

                {/* Submit */}
                <div className="flex gap-2 pt-4">
                  <button
                    type="submit"
                    className="flex-1 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center justify-center"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Salva
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300"
                  >
                    Annulla
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
    </AdminLayout>
  );
};

export default AdminGallery;
